/**
 * usePersonaInference Hook
 *
 * Infers the most likely persona from GitHub profile metadata.
 * Fires when a GitHub token is present, runs as a background scan,
 * and returns a suggested persona with confidence score.
 *
 * Signals used:
 *   - Language distribution (TypeScript/Python/Go/Java → dev/founder/architect)
 *   - Bio keywords (investor, founder, partner, cto, product)
 *   - Company field (enterprise org, startup, VC fund)
 *   - Org memberships count and names
 *   - Repo topics and descriptions
 *   - Follower/following ratio (investor pattern)
 *   - Star concentration (few high-star repos → founder)
 *
 * @module hooks/usePersonaInference
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { PERSONA_TYPES } from '../config/personas';

const GITHUB_API = 'https://api.github.com';

// Keyword sets per persona for bio/company/topic matching
const SIGNALS = {
  [PERSONA_TYPES.INVESTOR]: {
    bio:     ['investor', 'vc', 'venture', 'partner', 'angel', 'fund', 'capital', 'portfolio'],
    company: ['ventures', 'capital', 'fund', 'partners', 'investments', 'vc'],
    topics:  ['venture', 'investing', 'portfolio', 'due-diligence'],
  },
  [PERSONA_TYPES.FOUNDER]: {
    bio:     ['founder', 'ceo', 'co-founder', 'building', 'started', 'launched', 'bootstrapped'],
    company: ['inc', 'llc', 'ltd', 'startup', 'technologies', 'labs', 'io'],
    topics:  ['startup', 'saas', 'product', 'b2b', 'launch'],
  },
  [PERSONA_TYPES.ANALYST]: {
    bio:     ['analyst', 'research', 'consultant', 'advisor', 'strategy', 'm&a', 'due diligence'],
    company: ['consulting', 'advisory', 'research', 'partners', 'group'],
    topics:  ['analysis', 'finance', 'valuation', 'research', 'modeling'],
  },
  [PERSONA_TYPES.ARCHITECT]: {
    bio:     ['architect', 'cto', 'principal', 'staff engineer', 'platform', 'infrastructure'],
    company: ['enterprise', 'bank', 'financial', 'insurance', 'government', 'corp'],
    topics:  ['architecture', 'enterprise', 'platform', 'infrastructure', 'microservices'],
  },
  [PERSONA_TYPES.PRODUCT_OWNER]: {
    bio:     ['product', 'pm', 'product manager', 'product owner', 'roadmap', 'ux'],
    company: [],
    topics:  ['product', 'roadmap', 'analytics', 'growth', 'metrics'],
  },
  [PERSONA_TYPES.DEVELOPER]: {
    bio:     ['developer', 'engineer', 'programmer', 'open source', 'hacker', 'fullstack', 'backend', 'frontend'],
    company: [],
    topics:  ['open-source', 'library', 'framework', 'api', 'cli', 'sdk', 'tooling'],
  },
  [PERSONA_TYPES.PROFESSIONAL]: {
    bio:     ['software engineer', 'developer', 'programming', 'coding', 'tech lead'],
    company: [],
    topics:  ['career', 'portfolio', 'resume', 'learning'],
  },
};

const keywordMatch = (text = '', keywords = []) => {
  const lower = text.toLowerCase();
  return keywords.filter(k => lower.includes(k)).length;
};

/**
 * Score all personas from raw GitHub signals.
 * Returns { persona, confidence, scores }
 */
const inferPersonaFromSignals = ({ user, repos, orgs }) => {
  const scores = Object.fromEntries(
    Object.values(PERSONA_TYPES).map(p => [p, 0])
  );

  const bio = user?.bio || '';
  const company = user?.company || '';
  const followers = user?.followers || 0;
  const following = user?.following || 0;
  const publicRepos = user?.public_repos || 0;

  // Bio + company keyword matching
  for (const [persona, sigs] of Object.entries(SIGNALS)) {
    scores[persona] += keywordMatch(bio, sigs.bio) * 3;
    scores[persona] += keywordMatch(company, sigs.company) * 2;
  }

  // Follower/following ratio — investors follow many, are followed by many
  if (followers > 200 && following < 100) {
    scores[PERSONA_TYPES.INVESTOR] += 2;
  }

  // Lots of orgs → architect or product_owner (enterprise user)
  if (orgs.length > 3) {
    scores[PERSONA_TYPES.ARCHITECT] += 2;
    scores[PERSONA_TYPES.PRODUCT_OWNER] += 1;
  }

  // Language distribution
  const langCounts = {};
  repos.forEach(r => {
    if (r.language) langCounts[r.language] = (langCounts[r.language] || 0) + 1;
  });
  const topLang = Object.entries(langCounts).sort((a, b) => b[1] - a[1])[0]?.[0];

  if (['Java', 'Kotlin', 'Scala', 'C#', 'Go'].includes(topLang)) {
    scores[PERSONA_TYPES.ARCHITECT] += 2;
  }
  if (['TypeScript', 'JavaScript', 'Python', 'Rust'].includes(topLang)) {
    scores[PERSONA_TYPES.DEVELOPER] += 1;
    scores[PERSONA_TYPES.PROFESSIONAL] += 1;
  }

  // Star concentration — few repos with many stars → founder signal
  const totalStars = repos.reduce((s, r) => s + (r.stargazers_count || 0), 0);
  const maxStars = Math.max(...repos.map(r => r.stargazers_count || 0), 0);
  if (maxStars > 100 && repos.length < 20) {
    scores[PERSONA_TYPES.FOUNDER] += 3;
  }
  if (totalStars > 1000) {
    scores[PERSONA_TYPES.DEVELOPER] += 2;
  }

  // Topic matching across repos
  const allTopics = repos.flatMap(r => r.topics || []);
  for (const [persona, sigs] of Object.entries(SIGNALS)) {
    scores[persona] += allTopics.filter(t =>
      sigs.topics.some(k => t.includes(k))
    ).length;
  }

  // Low repo count + low stars + no strong signals → professional (safe default)
  if (publicRepos < 5) {
    scores[PERSONA_TYPES.PROFESSIONAL] += 1;
  }

  // Pick winner
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [topPersona, topScore] = sorted[0];
  const secondScore = sorted[1]?.[1] || 0;

  // Confidence: how far ahead is the winner vs second place
  const total = Object.values(scores).reduce((s, v) => s + v, 0) || 1;
  const margin = (topScore - secondScore) / total;
  const confidence = Math.min(0.95, 0.4 + margin * 2 + (topScore / total) * 0.4);

  return {
    persona: topScore > 0 ? topPersona : null,
    confidence: topScore > 0 ? confidence : 0,
    scores,
  };
};

/**
 * Fetch lightweight GitHub signals using the user's OAuth token.
 * Uses the GitHub API directly — token must be passed from the app's
 * auth flow (available via firebase.currentUser.getIdToken or stored
 * in localStorage as githubAccessToken after OAuth).
 */
const fetchGitHubSignals = async (token) => {
  const headers = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/vnd.github.v3+json',
  };

  const [userRes, reposRes, orgsRes] = await Promise.allSettled([
    fetch(`${GITHUB_API}/user`, { headers }),
    fetch(`${GITHUB_API}/user/repos?sort=pushed&per_page=30&type=owner`, { headers }),
    fetch(`${GITHUB_API}/user/orgs?per_page=10`, { headers }),
  ]);

  const user = userRes.status === 'fulfilled' && userRes.value.ok
    ? await userRes.value.json() : {};
  const repos = reposRes.status === 'fulfilled' && reposRes.value.ok
    ? await reposRes.value.json() : [];
  const orgs = orgsRes.status === 'fulfilled' && orgsRes.value.ok
    ? await orgsRes.value.json() : [];

  return { user, repos: Array.isArray(repos) ? repos : [], orgs: Array.isArray(orgs) ? orgs : [] };
};

/**
 * usePersonaInference
 *
 * @param {string|null} githubToken - GitHub OAuth access token
 * @returns {{ inferredPersona, confidence, isInferring, inferenceComplete }}
 */
const usePersonaInference = (githubToken) => {
  const [inferredPersona, setInferredPersona] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [isInferring, setIsInferring] = useState(false);
  const [inferenceComplete, setInferenceComplete] = useState(false);
  const hasRun = useRef(false);

  const runInference = useCallback(async (token) => {
    if (hasRun.current || !token) return;
    hasRun.current = true;
    setIsInferring(true);

    try {
      const signals = await fetchGitHubSignals(token);
      const result = inferPersonaFromSignals(signals);
      // Only surface suggestions with meaningful confidence
      if (result.persona && result.confidence >= 0.5) {
        setInferredPersona(result.persona);
        setConfidence(result.confidence);
      }
    } catch {
      // Inference is best-effort — silently degrade
    } finally {
      setIsInferring(false);
      setInferenceComplete(true);
    }
  }, []);

  useEffect(() => {
    if (githubToken && !hasRun.current) {
      runInference(githubToken);
    }
  }, [githubToken, runInference]);

  return { inferredPersona, confidence, isInferring, inferenceComplete };
};

export default usePersonaInference;
